// Item library evaluate codes for /roads-sewers/foul-sewers Pump Station / Rising
// Main build-up dev-mode display — mirrors prelimsMapping.ts's description -> code
// lookup pattern. Replaces the old behaviour of reading evaluateCode out of the
// Excel import (see foulSewersExcelUtils.ts) — codes now live here instead, looked
// up by each build-up row's static description.
//
// A few rows are intentionally left out of this list (mesh reinforcement rows and
// the Wet Well row) because their code depends on a user-selected value (mesh
// type / chamber dimensions) rather than being fixed per description — those are
// computed dynamically in FoulSewersTakeoff.tsx instead. Rows with no code at all
// ("no code" in the source list) are also left out — lookups for them return
// undefined and render as "—".

export interface FoulBuildUpEvaluateCodeItem {
  description: string;
  code: string;
}

export const PUMP_STATION_EVALUATE_CODES: FoulBuildUpEvaluateCodeItem[] = [
  { description: 'Site strip to remove vegetation (whole area)', code: 'ALP033' },
  { description: 'Excavate to reduce levels to take up 279mm top soil, deposit onsite', code: 'ALP013' },
  { description: 'Filling to make up levels with 6F2', code: 'ALP016' },
  { description: 'Excavate to reduce levels', code: 'ALP014' },
  { description: 'Cart Soil to onsite bunds', code: 'ALP033a' },
  { description: 'L & C Formation', code: 'ALP035' },
  { description: 'xxxmm MOT type 1 Sub Base', code: 'ALP017' },
  { description: 'Shutter to sides of concrete paths', code: 'GWALP032' },
  { description: 'Kerbs to upstand', code: 'ALP083' },
  { description: 'xxxmm thick PAV2 concrete slab', code: 'ASPS008' },
  { description: 'Gully & Pipework', code: 'ADRG001' },
  { description: 'Ducting/Drainage between VC, WW and Kiosk', code: 'AFPS003' },
  { description: 'Concrete bollards', code: 'AFPS010' },
  { description: '50mm Gravel & Geotextile Areas', code: 'ALP050+ALP031' },
  { description: 'xxmm thick concrete blinding', code: 'GWALPGW0015' },
  { description: 'Place cover and frame supplied by _______', code: 'AFPS002' },
  { description: 'Allowance for diamond drilling', code: 'APMCD001' },
  { description: 'Allowance for pipework between IM and PC', code: 'AFPS003' },
  { description: 'Fix only Lifting Davit & Cast in socket to suit', code: 'AFPS002' },
  { description: 'Fix only Stainless steel penstock', code: 'AFPS001' },
  { description: 'Fix only squat type Rising Main air relief valve', code: 'AFPS004' },
  { description: 'Supply & Install Pump Equipment', code: 'SCAP001' },
  { description: 'Excavate & Cart onsite', code: 'ALP014' },
  { description: 'EWS', code: 'AMHB001' },
  { description: '200mm Formwork', code: 'AMHEO050' },
  { description: 'xxxxx Concrete in Base', code: 'ALP070' },
  { description: '215wide brickwork', code: 'GWALPSMH018' },
  { description: 'Supply / Install Galvanised Ladders', code: 'AMEHO002' },
  { description: 'Place only cover and frame supplied by pump supplier', code: 'AFPS002' },
  { description: 'Supply and lay Granolithic screed to form fall', code: 'AMHEO053' },
  { description: 'Excavate', code: 'ALP014' },
  { description: 'Type 1', code: 'ALP017' },
  { description: '150mm Formwork', code: 'AMHEO050' },
  { description: 'xxxxx Concrete Base', code: 'ALP070' },

  // ── Overflow Storage Tank / Weholite Attenuation Tank ──────────────────────
  { description: 'Excavate pit (all-in)', code: 'APATT001' },
  { description: 'Level and compact', code: 'ALP035' },
  { description: 'Granular base', code: 'ALP017' },
  { description: 'Supply of Weholite Tank', code: 'SCSATT034' },
  { description: 'Weholite welding', code: 'SCSATT011' },
  { description: 'M&J R+S gang - Install', code: 'APATT006' },
  { description: 'Total Bed & Surround', code: 'ALP111' },
  { description: 'As dug backfill to void', code: 'APATT003' },
  { description: 'Stockpile surplus onsite', code: 'ALP033a' },
  { description: 'Foundation for cover slab', code: 'APATT008' },
  { description: '1200mmØ cover slab', code: 'AMHCS1200' },
  { description: 'Seating Rings', code: 'CDRA0099' },
  { description: 'D400 Cover & Frame', code: 'AMHID400' },
];

export function findPumpStationEvaluateCode(description: string): string | undefined {
  return PUMP_STATION_EVALUATE_CODES.find((i) => i.description === description)?.code;
}

export const RISING_MAIN_EVALUATE_CODES: FoulBuildUpEvaluateCodeItem[] = [
  { description: '90mmOD SDR11 rising main  - 1.50m deep', code: '90\\SDR11\\ 1.50' },
  { description: '90mmOD SDR11 rising main  - 1.75m deep', code: '90\\SDR11\\ 1.75' },
  { description: '90mmOD SDR11 rising main  - 2.00m deep', code: '90\\SDR11\\ 2.00' },
  { description: '90mmOD SDR11 rising main  - 2.25m deep', code: '90\\SDR11\\ 2.25' },
  { description: '110mmOD SDR11 rising main  - 1.50m deep', code: '110\\SDR11\\ 1.50' },
  { description: '110mmOD SDR11 rising main  - 1.75m deep', code: '110\\SDR11\\ 1.75' },
  { description: '110mmOD SDR11 rising main  - 2.00m deep', code: '110\\SDR11\\ 2.00' },
  { description: '110mmOD SDR11 rising main  - 2.25m deep', code: '110\\SDR11\\ 2.25' },
  { description: '125mmOD SDR11 rising main  - 1.50m deep', code: '125\\SDR11\\ 1.50' },
  { description: '125mmOD SDR11 rising main  - 1.75m deep', code: '125\\SDR11\\ 1.75' },
  { description: '125mmOD SDR11 rising main  - 2.00m deep', code: '125\\SDR11\\ 2.00' },
  { description: '125mmOD SDR11 rising main  - 2.25m deep', code: '125\\SDR11\\ 2.25' },
  { description: '125mmOD SDR11 rising main  - 2.50m deep', code: '125\\SDR11\\ 2.50' },
  { description: '125mmOD SDR11 rising main  - 2.75m deep', code: '125\\SDR11\\ 2.75' },
  { description: '125mmOD SDR11 rising main  - 3.00m deep', code: '125\\SDR11\\ 3.00' },
  { description: '125mmOD SDR11 rising main  - 3.25m deep', code: '125\\SDR11\\ 3.25' },
  { description: '125mmOD SDR11 rising main  - 3.50m deep', code: '125\\SDR11\\ 3.50' },
  { description: '125mmOD SDR11 rising main  - 3.75m deep', code: '125\\SDR11\\ 3.75' },
  { description: '125mmOD SDR11 rising main  - 4.00m deep', code: '125\\SDR11\\ 4.00' },
  { description: '180mmOD SDR11 rising main  - 1.50m deep', code: '180\\SDR11\\ 1.50' },
  { description: '180mmOD SDR11 rising main  - 1.75m deep', code: '180\\SDR11\\ 1.75' },
  { description: '180mmOD SDR11 rising main  - 2.00m deep', code: '180\\SDR11\\ 2.00' },
  { description: '180mmOD SDR11 rising main  - 2.25m deep', code: '180\\SDR11\\ 2.25' },
  { description: '90mmOD SDR17 rising main  - 1.50m deep', code: '90\\SDR17\\ 1.50' },
  { description: '90mmOD SDR17 rising main  - 1.75m deep', code: '90\\SDR17\\ 1.75' },
  { description: '90mmOD SDR17 rising main  - 2.00m deep', code: '90\\SDR17\\ 2.00' },
  { description: '90mmOD SDR17 rising main  - 2.25m deep', code: '90\\SDR17\\ 2.25' },
  { description: '110mmOD SDR17 rising main  - 1.50m deep', code: '110\\SDR17\\ 1.50' },
  { description: '110mmOD SDR17 rising main  - 1.75m deep', code: '110\\SDR17\\ 1.75' },
  { description: '110mmOD SDR17 rising main  - 2.00m deep', code: '110\\SDR17\\ 2.00' },
  { description: '110mmOD SDR17 rising main  - 2.25m deep', code: '110\\SDR17\\ 2.25' },
  { description: '125mmOD SDR17 rising main  - 1.00m deep', code: '125\\SDR17\\ 1.00' },
  { description: '125mmOD SDR17 rising main  - 1.25m deep', code: '125\\SDR17\\ 1.25' },
  { description: '125mmOD SDR17 rising main  - 1.50m deep', code: '125\\SDR17\\ 1.50' },
  { description: '125mmOD SDR17 rising main  - 1.75m deep', code: '125\\SDR17\\ 1.75' },
  { description: '125mmOD SDR17 rising main  - 2.00m deep', code: '125\\SDR17\\ 2.00' },
  { description: '125mmOD SDR17 rising main  - 2.25m deep', code: '125\\SDR17\\ 2.25' },
  { description: '180mmOD SDR17 rising main  - 1.50m deep', code: '180\\SDR17\\ 1.50' },
  { description: '180mmOD SDR17 rising main  - 1.75m deep', code: '180\\SDR17\\ 1.75' },
  { description: '180mmOD SDR17 rising main  - 2.00m deep', code: '180\\SDR17\\ 2.00' },
  { description: '180mmOD SDR17 rising main  - 2.25m deep', code: '180\\SDR17\\ 2.25' },
  { description: 'Pressure testing of rising main', code: 'SCAP003' },
  { description: 'EO concrete bed & surround to pipe', code: 'AMHEO027' },
  { description: 'EO for working in existing carriageway', code: 'AMHEO006' },
  { description: 'EO for trench reinstatement in carriageway', code: 'AMHEO007' },
  { description: 'Break into existing foul manhole for connection', code: 'AMHEO003' },
];

export function findRisingMainEvaluateCode(description: string): string | undefined {
  return RISING_MAIN_EVALUATE_CODES.find((i) => i.description === description)?.code;
}

// Mesh reinforcement rows (ps_10, ps_pc_mesh, ps_vc_mesh, ps_fm_mesh, ps_kb_mesh)
// render a dynamic description ("<selected type> mesh ...") — their code depends
// on the user-selected mesh type, same scheme as /groundworks/foundations'
// MESH_REINFORCEMENT_CODE_MAP (FoundationsNew.tsx).
export const MESH_REINFORCEMENT_CODE_MAP: Record<string, string> = {
  'A142': 'GWALPRM001',
  'A193': 'GWALPRM002',
  'A252': 'GWALPRM003',
  'A393': 'GWALPRM004',
  'B283': 'GWALPRM017',
  'B385': 'GWALPRM005',
  'B503': 'GWALPRM006',
  'B785': 'GWALPRM007',
  'B1131': 'GWALPRM008',
};

export function findMeshReinforcementEvaluateCode(meshType?: string): string | undefined {
  if (!meshType) return undefined;
  return MESH_REINFORCEMENT_CODE_MAP[meshType];
}

// Mesh reinforcement rows keyed by which PumpChamberConfig field selects their mesh type.
const MESH_ROW_CONFIG_KEY: Record<string, 'meshType' | 'pumpChamberMeshType' | 'valveChamberMeshType' | 'flowMeterChamberMeshType' | 'kioskBaseMeshType'> = {
  ps_10: 'meshType',
  ps_pc_mesh: 'pumpChamberMeshType',
  ps_vc_mesh: 'valveChamberMeshType',
  ps_fm_mesh: 'flowMeterChamberMeshType',
  ps_kb_mesh: 'kioskBaseMeshType',
};

/** Minimal shape of the Pump Chamber config fields this resolver reads — kept
 * decoupled from the full `PumpChamberConfig` type (tender.types.ts) to avoid a
 * cross-module type import here; callers pass their own config object through. */
export interface PumpChamberConfigForEvaluateCode {
  coverLevel: number;
  invert: number;
  sump: number;
  chamberDia: number;
  meshType?: string;
  pumpChamberMeshType?: string;
  valveChamberMeshType?: string;
  flowMeterChamberMeshType?: string;
  kioskBaseMeshType?: string;
}

/**
 * Single source of truth for a Pump Station / Rising Main build-up row's evaluate
 * code — used by both `FoulSewersTakeoff.tsx`'s live dev-mode display and
 * `buildBuildUpSummaryData` (`foulSummaryUtils.ts`, feeding the `/roads-sewers/review`
 * Foul Sewers Bill Summary's Pump Station / Rising Main tabs), so both stay in sync.
 * Mesh reinforcement rows and the Wet Well row depend on `pumpChamberConfig`
 * (user-selected mesh type / chamber dimensions) rather than a fixed description.
 */
export function resolveBuildUpEvaluateCode(
  row: { id: string; description: string },
  pumpChamberConfig?: PumpChamberConfigForEvaluateCode,
): string {
  const meshKey = MESH_ROW_CONFIG_KEY[row.id];
  if (meshKey) {
    return findMeshReinforcementEvaluateCode(pumpChamberConfig?.[meshKey]) ?? '';
  }
  if (row.id === 'ps_pc_wetwell') {
    if (!pumpChamberConfig) return '';
    const depth = parseFloat(
      (pumpChamberConfig.coverLevel - pumpChamberConfig.invert + pumpChamberConfig.sump).toFixed(3),
    );
    const depthCategory = Math.round(depth / 0.25) * 0.25;
    return `Trad\\${pumpChamberConfig.chamberDia ?? 0}\\${depthCategory.toFixed(2)}`;
  }
  // Ladder wording switches Galvanised/Stainless with the Manhole Schedule's Ladders setting — resolve by id.
  if (row.id === 'ps_vc_ladder' || row.id === 'ps_fm_ladder') return 'AMEHO002';
  if (row.id.startsWith('rm_')) {
    return findRisingMainEvaluateCode(row.description) ?? '';
  }
  return findPumpStationEvaluateCode(row.description) ?? '';
}
